/**
 * Putting a short string into the clipboard from a telegram webview.
 *
 * Two ways, and both are needed. navigator.clipboard is the modern one, but it exists only in
 * a secure context and telegram's android webview refuses it often enough that a button which
 * relies on it alone silently does nothing on somebody's phone. The old selection dance works
 * there, and works anywhere the modern api is merely disallowed.
 *
 * Whether the text landed is returned rather than thrown: the caller says "скопировано" or
 * "не скопировалось", and neither is an error worth a stack trace.
 */

export async function copyText(value: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // no permission, or an insecure context: the old way may still be allowed
    }
  }
  return copyBySelection(value);
}

/**
 * Copy by selecting the text in a field nobody sees.
 *
 * The field is readonly so that ios does not raise the keyboard over the app, and positioned
 * instead of hidden because a display:none element cannot be selected. execCommand is
 * deprecated and still the only fallback there is.
 */
function copyBySelection(value: string): boolean {
  const area = document.createElement("textarea");
  area.value = value;
  area.readOnly = true;
  area.style.position = "fixed";
  area.style.top = "0";
  area.style.left = "0";
  area.style.opacity = "0";
  document.body.appendChild(area);

  try {
    area.focus();
    area.select();
    // ios ignores select() on a readonly field, this is the range it does listen to
    area.setSelectionRange(0, value.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
